import React, { useState, useEffect } from 'react';
import {
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, DollarSign,
  Package, Users, Database, PieChart as PieChartIcon, BarChart3
} from 'lucide-react';
import SunburstChart from './Visualizations/SunburstChart';
import NetworkGraph from './Visualizations/NetworkGraph';
import HeatmapMatrix from './Visualizations/HeatmapMatrix';

const API_URL = process.env.REACT_APP_API_URL || '';

const TRMAnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [categoryStats, setCategoryStats] = useState([]);
  const [manufacturerStats, setManufacturerStats] = useState([]);
  const [lifecycleStats, setLifecycleStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'sunburst', 'network', 'heatmap'

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch all analytics data in parallel
      const [statsRes, categoryRes, mfrRes, lifecycleRes] = await Promise.all([
        fetch(`${API_URL}/api/trm/stats`),
        fetch(`${API_URL}/api/trm/stats/by-category`),
        fetch(`${API_URL}/api/trm/stats/by-manufacturer`),
        fetch(`${API_URL}/api/trm/stats/lifecycle`)  // Fixed: was by-lifecycle
      ]);

      if (!statsRes.ok || !categoryRes.ok || !mfrRes.ok || !lifecycleRes.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const [statsData, categoryData, mfrData, lifecycleData] = await Promise.all([
        statsRes.json(),
        categoryRes.json(),
        mfrRes.json(),
        lifecycleRes.json()
      ]);

      setStats(statsData);
      setCategoryStats(categoryData);
      setManufacturerStats(mfrData);
      setLifecycleStats(lifecycleData);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p style={{ marginTop: '16px', color: 'var(--cares-text-label)' }}>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))', borderRadius: '8px', border: '1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))' }}>
        <h3 style={{ color: 'var(--cares-orange)', marginBottom: '8px' }}>Error Loading Data</h3>
        <p style={{ color: 'var(--cares-orange)' }}>{error}</p>
      </div>
    );
  }

  // Calculate insights (moved before return)
  const retiredPercentage = lifecycleStats.find(s => s.lifecycle_stage === 'Retired')?.percentage || 0;
  const topCategory = categoryStats[0];
  const topManufacturer = manufacturerStats[0];

  // Mock cost data - can be real when you have license cost data
  const estimatedAnnualCost = manufacturerStats.reduce((sum, mfr) => {
    return sum + (mfr.product_count * 50000); // Estimate $50k per product
  }, 0);

  const potentialSavings = retiredPercentage > 20 ? estimatedAnnualCost * (retiredPercentage / 100) * 0.3 : 0;

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
      {/* Header with View Tabs */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: 'var(--cares-text-1)', marginBottom: '8px' }}>
          TRM Analytics Dashboard
        </h1>
        <p style={{ color: 'var(--cares-text-label)', fontSize: '16px', marginBottom: '24px' }}>
          Portfolio analysis, spending optimization, and vendor consolidation opportunities
        </p>

        {/* View Tabs */}
        <div style={{ display: 'flex', gap: '12px', borderBottom: '2px solid var(--cares-line-card)', paddingBottom: '0' }}>
          <button
            onClick={() => setActiveView('overview')}
            style={{
              padding: '12px 24px',
              background: activeView === 'overview' ? 'linear-gradient(135deg, var(--cares-blue), var(--cares-blue))' : 'transparent',
              color: activeView === 'overview' ? 'var(--cares-text-max)' : 'var(--cares-text-label)',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            📊 Overview & Stats
          </button>
          <button
            onClick={() => setActiveView('sunburst')}
            style={{
              padding: '12px 24px',
              background: activeView === 'sunburst' ? 'linear-gradient(135deg, var(--cares-blue), var(--cares-blue))' : 'transparent',
              color: activeView === 'sunburst' ? 'var(--cares-text-max)' : 'var(--cares-text-label)',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            ☀️ Sunburst Chart
          </button>
          <button
            onClick={() => setActiveView('network')}
            style={{
              padding: '12px 24px',
              background: activeView === 'network' ? 'linear-gradient(135deg, var(--cares-blue), var(--cares-blue))' : 'transparent',
              color: activeView === 'network' ? 'var(--cares-text-max)' : 'var(--cares-text-label)',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            🔗 Network Graph
          </button>
          <button
            onClick={() => setActiveView('heatmap')}
            style={{
              padding: '12px 24px',
              background: activeView === 'heatmap' ? 'linear-gradient(135deg, var(--cares-blue), var(--cares-blue))' : 'transparent',
              color: activeView === 'heatmap' ? 'var(--cares-text-max)' : 'var(--cares-text-label)',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            🔥 Heatmap
          </button>
        </div>
      </div>

      {/* Content Area - Show different views based on activeView */}
      {activeView === 'sunburst' && (
        <div style={{ background: 'var(--cares-surface-card)', borderRadius: '12px', padding: '0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <SunburstChart />
        </div>
      )}

      {activeView === 'network' && (
        <div style={{ background: 'var(--cares-surface-card)', borderRadius: '12px', padding: '0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <NetworkGraph />
        </div>
      )}

      {activeView === 'heatmap' && (
        <div style={{ background: 'var(--cares-surface-card)', borderRadius: '12px', padding: '0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <HeatmapMatrix />
        </div>
      )}

      {activeView === 'overview' && (
        <>
          {/* Key Insights Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '32px'
          }}>
            {/* Estimated Annual Cost */}
            <div style={{
              background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <DollarSign style={{ width: '24px', height: '24px' }} />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>Est. Annual Cost</span>
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700' }}>
            ${(estimatedAnnualCost / 1000000).toFixed(1)}M
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '8px' }}>
            Based on {stats.total_products} products
          </div>
        </div>

        {/* Potential Savings */}
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-magenta) 0%, var(--cares-magenta) 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <TrendingDown style={{ width: '24px', height: '24px' }} />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>Potential Savings</span>
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700' }}>
            ${(potentialSavings / 1000).toFixed(0)}K
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '8px' }}>
            By retiring {retiredPercentage.toFixed(0)}% old versions
          </div>
        </div>

        {/* Vendor Consolidation */}
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-teal) 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Users style={{ width: '24px', height: '24px' }} />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>Active Vendors</span>
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700' }}>
            {stats.active_manufacturers}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '8px' }}>
            Top vendor: {topManufacturer?.manufacturer_name}
          </div>
        </div>

        {/* Portfolio Health */}
        <div style={{
          background: 'linear-gradient(135deg, var(--cares-green) 0%, var(--cares-teal) 100%)',
          borderRadius: '12px',
          padding: '24px',
          color: 'var(--cares-text-max)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <CheckCircle style={{ width: '24px', height: '24px' }} />
            <span style={{ fontSize: '14px', opacity: 0.9 }}>Portfolio Health</span>
          </div>
          <div style={{ fontSize: '36px', fontWeight: '700' }}>
            {((stats.active_versions / stats.total_versions) * 100).toFixed(0)}%
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '8px' }}>
            {stats.active_versions} active of {stats.total_versions} versions
          </div>
        </div>
      </div>

      {/* Category Distribution */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--cares-text-1)', marginBottom: '20px' }}>
          <BarChart3 style={{ display: 'inline', width: '20px', height: '20px', marginRight: '8px' }} />
          Portfolio by Category
        </h2>
        <div style={{ display: 'grid', gap: '12px' }}>
          {categoryStats.slice(0, 8).map((cat, idx) => {
            const maxCount = categoryStats[0]?.product_count || 1;
            const percentage = (cat.product_count / maxCount) * 100;

            return (
              <div key={idx} style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ fontWeight: '500', color: 'var(--cares-text-3)' }}>{cat.category}</span>
                  <span style={{ color: 'var(--cares-text-label)', fontSize: '14px' }}>
                    {cat.product_count} products • {cat.version_count} versions
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  background: 'var(--cares-surface-board)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${percentage}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, var(--cares-purple), var(--cares-purple))`,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Manufacturers & Alerts Side by Side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Top Manufacturers */}
        <div style={{
          background: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
            Top Vendors by Portfolio Size
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {manufacturerStats.slice(0, 5).map((mfr, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px',
                background: 'var(--cares-surface-board)',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--cares-purple), var(--cares-purple))',
                    color: 'var(--cares-text-max)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600'
                  }}>
                    {idx + 1}
                  </div>
                  <div>
                    <div style={{ fontWeight: '500', color: 'var(--cares-text-1)' }}>{mfr.manufacturer_name}</div>
                    <div style={{ fontSize: '13px', color: 'var(--cares-text-label)' }}>
                      {mfr.product_count} products • {mfr.version_count} versions
                    </div>
                  </div>
                </div>
                <div style={{
                  padding: '4px 12px',
                  background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
                  color: 'var(--cares-blue)',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  ${((mfr.product_count * 50000) / 1000).toFixed(0)}K
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts & Recommendations */}
        <div style={{
          background: 'var(--cares-surface-card)',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
            <AlertTriangle style={{ display: 'inline', width: '18px', height: '18px', marginRight: '8px' }} />
            Alerts & Recommendations
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {retiredPercentage > 15 && (
              <div style={{
                padding: '12px',
                background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))',
                border: '1px solid var(--cares-amber)',
                borderRadius: '8px'
              }}>
                <div style={{ fontWeight: '500', color: 'var(--cares-amber)', marginBottom: '4px' }}>
                  High Retirement Rate
                </div>
                <div style={{ fontSize: '13px', color: 'var(--cares-amber)' }}>
                  {retiredPercentage.toFixed(0)}% of versions are retired. Consider cleanup.
                </div>
              </div>
            )}
            {stats.total_manufacturers > 40 && (
              <div style={{
                padding: '12px',
                background: 'color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card))',
                border: '1px solid var(--cares-blue)',
                borderRadius: '8px'
              }}>
                <div style={{ fontWeight: '500', color: 'var(--cares-blue)', marginBottom: '4px' }}>
                  Vendor Consolidation Opportunity
                </div>
                <div style={{ fontSize: '13px', color: 'var(--cares-blue)' }}>
                  {stats.total_manufacturers} vendors detected. Consolidation could save ${((stats.total_manufacturers * 5000) / 1000).toFixed(0)}K/year.
                </div>
              </div>
            )}
            {categoryStats.some(c => c.product_count > 10) && (
              <div style={{
                padding: '12px',
                background: 'color-mix(in srgb, var(--cares-purple) 22%, var(--cares-surface-card))',
                border: '1px solid var(--cares-purple)',
                borderRadius: '8px'
              }}>
                <div style={{ fontWeight: '500', color: 'var(--cares-purple)', marginBottom: '4px' }}>
                  Potential Overlap Detected
                </div>
                <div style={{ fontSize: '13px', color: 'var(--cares-purple)' }}>
                  {topCategory?.category} category has {topCategory?.product_count} products. Review for redundancy.
                </div>
              </div>
            )}
            <div style={{
              padding: '12px',
              background: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))',
              border: '1px solid var(--cares-green)',
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: '500', color: 'var(--cares-green)', marginBottom: '4px' }}>
                Portfolio Health Good
              </div>
              <div style={{ fontSize: '13px', color: 'var(--cares-green)' }}>
                {((stats.active_versions / stats.total_versions) * 100).toFixed(0)}% of versions are actively maintained.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lifecycle Distribution */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--cares-text-1)', marginBottom: '20px' }}>
          <PieChartIcon style={{ display: 'inline', width: '20px', height: '20px', marginRight: '8px' }} />
          Version Lifecycle Distribution
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {lifecycleStats.map((stage, idx) => {
            const colors = {
              'Active': { bg: 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))', color: 'var(--cares-green)', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
              'Retired': { bg: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))', color: 'var(--cares-orange)', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
              'Deprecated': { bg: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))', color: 'var(--cares-amber)', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)' },
              'Planned': { bg: 'color-mix(in srgb, var(--cares-blue) 22%, var(--cares-surface-card))', color: 'var(--cares-blue)', gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)' }
            };
            const style = colors[stage.lifecycle_stage] || colors['Active'];

            return (
              <div key={idx} style={{
                padding: '20px',
                background: style.bg,
                borderRadius: '12px',
                border: `1px solid ${style.color}33`
              }}>
                <div style={{ fontSize: '14px', color: style.color, marginBottom: '8px', fontWeight: '500' }}>
                  {stage.lifecycle_stage}
                </div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: style.color, marginBottom: '4px' }}>
                  {stage.version_count}
                </div>
                <div style={{ fontSize: '13px', color: style.color, opacity: 0.8 }}>
                  {stage.percentage.toFixed(1)}% of portfolio
                </div>
              </div>
            );
          })}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default TRMAnalyticsDashboard;

